/**
 * Track who last edited a timesheet line from Koneko A (until the API stores this).
 */
import {canonicalizeKonekoUsername} from "@/config/konekoAConfig";

export const KONEKO_TIMESHEET_EDIT_ATTRIBUTION_KEY =
  "nekohr_koneko_timesheet_edit_attribution";

/** Machine-readable suffix in log_description (survives refresh for all users). */
export const KONEKO_EDITED_BY_NOTE_RE = /\[Koneko edited by ([^\]]+)\]/i;

export function formatKonekoEditorLabel(username, displayName) {
  const key =
    canonicalizeKonekoUsername(username) || String(username || "").trim();
  const name = String(displayName || "").trim();
  if (name && key && name.toLowerCase() !== key.toLowerCase()) {
    return `${name} (${key})`;
  }
  return name || key || "unknown";
}

function readMap() {
  try {
    const raw = localStorage.getItem(KONEKO_TIMESHEET_EDIT_ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    localStorage.setItem(
      KONEKO_TIMESHEET_EDIT_ATTRIBUTION_KEY,
      JSON.stringify(map || {})
    );
  } catch {
    /* ignore */
  }
}

export function recordKonekoTimesheetEditAttribution(
  timesheetId,
  {username, displayName, editedAt} = {}
) {
  const id = Number(timesheetId);
  if (!Number.isFinite(id) || id <= 0) return;
  const key =
    canonicalizeKonekoUsername(username) || String(username || "").trim();
  if (!key) return;
  const map = readMap();
  map[String(id)] = {
    editorUsername: key,
    editorDisplayName: String(displayName || "").trim() || key,
    editorLabel: formatKonekoEditorLabel(key, displayName),
    editedAt: editedAt || new Date().toISOString(),
  };
  writeMap(map);
}

export function getKonekoTimesheetEditAttribution(timesheetId) {
  const id = Number(timesheetId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const hit = readMap()[String(id)];
  return hit && typeof hit === "object" ? hit : null;
}

export function parseEditedByFromNotes(notes) {
  const text = String(notes || "");
  const m = text.match(KONEKO_EDITED_BY_NOTE_RE);
  if (!m) return null;
  const key = canonicalizeKonekoUsername(m[1]) || String(m[1] || "").trim();
  if (!key) return null;
  return {
    editorUsername: key,
    editorDisplayName: key,
    editorLabel: key,
    fromNotes: true,
  };
}

export function appendKonekoEditedByNote(description, editorUsername) {
  const key =
    canonicalizeKonekoUsername(editorUsername) ||
    String(editorUsername || "").trim();
  if (!key) return String(description || "").trim();
  const base = String(description || "")
    .replace(KONEKO_EDITED_BY_NOTE_RE, "")
    .trim();
  const tag = `[Koneko edited by ${key}]`;
  if (!base) return tag;
  if (base.includes(tag)) return base;
  return `${base} ${tag}`;
}
